/**
 * BarraSelector Component
 *
 * Allows users to select their grid node (barra) for YoY spot price projections.
 * This selection determines which projection data is used for financial calculations.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MapPin, Info, Check, AlertCircle, ChevronDown } from 'lucide-react';
import { clsx } from 'clsx';
import { projectionService, type BarraOption, type ProjectionStatus } from '../../../services/api/projection.service.js';
import { ProjectionChart } from './ProjectionChart.js';

interface BarraSelectorProps {
  projectId: string;
  className?: string;
  onBarraChange?: (barra: string | null) => void;
}

export function BarraSelector({ projectId, className, onBarraChange }: BarraSelectorProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedBarra, setSelectedBarra] = useState<string | null>(null);

  // Fetch available barras
  const { data: barrasData, isLoading: loadingBarras } = useQuery({
    queryKey: ['projection-barras'],
    queryFn: () => projectionService.getBarras(),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Fetch project's current status
  const { data: projectStatus, isLoading: loadingStatus } = useQuery({
    queryKey: ['projection-status', projectId],
    queryFn: () => projectionService.getProjectStatus(projectId),
    enabled: !!projectId,
  });

  // Mutation to save barra selection
  const selectBarraMutation = useMutation({
    mutationFn: (barra: string | null) => projectionService.selectBarra(projectId, barra),
    onSuccess: (_, barra) => {
      setSelectedBarra(barra);
      queryClient.invalidateQueries({ queryKey: ['projection-status', projectId] });
      queryClient.invalidateQueries({ queryKey: ['current-project'] });
      onBarraChange?.(barra);
    },
  });

  // Set initial selection from project status
  useEffect(() => {
    if (projectStatus?.selectedBarra) {
      setSelectedBarra(projectStatus.selectedBarra);
    }
  }, [projectStatus]);

  function handleSelectBarra(barra: string): void {
    selectBarraMutation.mutate(barra);
    setIsOpen(false);
  }

  function handleClearBarra(): void {
    selectBarraMutation.mutate(null);
    setIsOpen(false);
  }

  // If no projection data available, show info message
  if (!loadingBarras && !barrasData?.hasProjection) {
    return (
      <div className={clsx('bg-amber-50 border border-amber-200 rounded-lg p-3', className)}>
        <div className="flex items-start gap-2">
          <Info className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm text-amber-800 font-medium">
              {t('projection.noDataTitle', 'Sin proyeccion de precios')}
            </p>
            <p className="text-xs text-amber-700 mt-1">
              {t('projection.noDataDescription', 'Los datos de proyeccion de precios aun no estan disponibles. Contacte al administrador.')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const barras = barrasData?.barras || [];
  const isLoading = loadingBarras || loadingStatus || selectBarraMutation.isPending;

  return (
    <div className={clsx('bg-white border border-swiss-mist rounded-lg p-4', className)}>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
          <MapPin className="w-4 h-4 text-purple-600" />
        </div>
        <div>
          <h4 className="text-sm font-semibold text-swiss-charcoal">
            {t('projection.selectBarra', 'Nodo de conexion')}
          </h4>
          <p className="text-xs text-swiss-stone">
            {t('projection.selectBarraDescription', 'Seleccione su barra para proyeccion de precios')}
          </p>
        </div>
      </div>

      {/* Dropdown selector */}
      <div className="relative">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          disabled={isLoading}
          className={clsx(
            'w-full flex items-center justify-between px-3 py-2 text-sm rounded-lg border transition-colors',
            selectedBarra
              ? 'border-purple-300 bg-purple-50 text-purple-800'
              : 'border-swiss-mist bg-white text-swiss-charcoal hover:border-swiss-stone',
            isLoading && 'opacity-50 cursor-not-allowed'
          )}
        >
          <span className="flex items-center gap-2">
            {selectedBarra ? (
              <>
                <Check className="w-4 h-4 text-purple-600" />
                {selectedBarra}
              </>
            ) : (
              <span className="text-swiss-stone">
                {t('projection.selectPlaceholder', 'Seleccionar barra...')}
              </span>
            )}
          </span>
          <ChevronDown
            className={clsx(
              'w-4 h-4 text-swiss-stone transition-transform',
              isOpen && 'transform rotate-180'
            )}
          />
        </button>

        {/* Dropdown menu */}
        {isOpen && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-swiss-mist rounded-lg shadow-lg max-h-60 overflow-auto">
            {/* Clear option */}
            {selectedBarra && (
              <button
                type="button"
                onClick={handleClearBarra}
                className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 border-b border-swiss-mist"
              >
                {t('projection.clearSelection', 'Limpiar seleccion')}
              </button>
            )}

            {/* Barra options */}
            {barras.map((barra) => (
              <button
                key={barra.value}
                type="button"
                onClick={() => handleSelectBarra(barra.value)}
                className={clsx(
                  'w-full px-3 py-2 text-left text-sm hover:bg-swiss-pearl transition-colors flex items-center justify-between',
                  selectedBarra === barra.value && 'bg-purple-50 text-purple-800'
                )}
              >
                <span>{barra.label}</span>
                {selectedBarra === barra.value && (
                  <Check className="w-4 h-4 text-purple-600" />
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Projection Chart - shown when barra is selected */}
      {selectedBarra && (
        <ProjectionChart barra={selectedBarra} className="mt-4" />
      )}

      {/* Error state */}
      {selectBarraMutation.isError && (
        <div className="mt-2 flex items-center gap-2 text-xs text-red-600">
          <AlertCircle className="w-3 h-3" />
          <span>{t('projection.saveError', 'Error al guardar la seleccion')}</span>
        </div>
      )}
    </div>
  );
}
